import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { TrendingUp, Search, Download, Filter, Info } from 'lucide-react';

const SchemePerformancePage = () => {
  const [amcList, setAmcList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schemeData, setSchemeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAmc, setSelectedAmc] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('default');
  const [sortConfig, setSortConfig] = useState({ key: '1y', direction: 'desc' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSchemeData();
  }, [selectedAmc, selectedCategory, selectedPeriod]);

  const fetchInitialData = async () => {
    try {
      const [amcRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/research/amc-list`),
        fetch(`${API_URL}/api/research/scheme-categories`)
      ]);
      
      const amcData = await amcRes.json();
      const catData = await catRes.json();
      
      if (amcData.status === 'success') setAmcList(amcData.data);
      if (catData.status === 'success') setCategories(catData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchSchemeData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/research/scheme-performance?amc=${selectedAmc}&category=${selectedCategory}&period=${selectedPeriod}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setSchemeData(data.data);
      }
    } catch (error) {
      console.error('Error fetching scheme data:', error);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...schemeData].sort((a, b) => {
    const aVal = a[sortConfig.key] || 0;
    const bVal = b[sortConfig.key] || 0;
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const formatReturn = (value) => {
    if (value === null || value === undefined || value === '-') return '-';
    const num = parseFloat(value);
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={color}>{num.toFixed(2)}%</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl mb-4">
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Scheme Performance Analysis
            </h1>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm text-left">
                  Track historical performance of mutual fund schemes across all AMCs for different time intervals. 
                  All returns shown are <strong>Compounded Annual Growth Rate (CAGR)</strong> for respective periods.
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              {/* AMC Filter */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Fund House</label>
                <select
                  value={selectedAmc}
                  onChange={(e) => setSelectedAmc(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-500 appearance-none cursor-pointer"
                >
                  <option value="all" className="bg-gray-900">All Fund Houses</option>
                  {amcList.map((amc, idx) => (
                    <option key={idx} value={amc} className="bg-gray-900">{amc}</option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Scheme Type</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-500 appearance-none cursor-pointer"
                >
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat.value} className="bg-gray-900">{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Time Period */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Time Period</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-500 appearance-none cursor-pointer"
                >
                  <option value="default" className="bg-gray-900">All Periods</option>
                  <option value="1y" className="bg-gray-900">1 Year</option>
                  <option value="3y" className="bg-gray-900">3 Years</option>
                  <option value="5y" className="bg-gray-900">5 Years</option>
                  <option value="10y" className="bg-gray-900">10 Years</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-white/60 text-sm">
              Showing <span className="text-white font-semibold">{sortedData.length}</span> schemes
            </p>
            <button className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/30 transition-colors text-sm">
              <Download className="w-4 h-4" />
              Export Data
            </button>
          </div>

          {/* Data Table */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="text-left py-4 px-4 text-white/70 font-medium text-sm">Scheme Name</th>
                    <th 
                      className="text-right py-4 px-4 text-white/70 font-medium text-sm cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('1y')}
                    >
                      1 Year {sortConfig.key === '1y' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="text-right py-4 px-4 text-white/70 font-medium text-sm cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('3y')}
                    >
                      3 Years {sortConfig.key === '3y' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="text-right py-4 px-4 text-white/70 font-medium text-sm cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('5y')}
                    >
                      5 Years {sortConfig.key === '5y' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="text-right py-4 px-4 text-white/70 font-medium text-sm cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('10y')}
                    >
                      10 Years {sortConfig.key === '10y' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="text-right py-4 px-4 text-white/70 font-medium text-sm cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('inception')}
                    >
                      Since Inception {sortConfig.key === 'inception' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-white/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-6 h-6 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin"></div>
                          Loading scheme data...
                        </div>
                      </td>
                    </tr>
                  ) : sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-white/50">
                        No schemes found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((scheme, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-white font-medium text-sm">{scheme.scheme_name}</p>
                            <p className="text-white/50 text-xs mt-1">{scheme.amc}</p>
                          </div>
                        </td>
                        <td className="text-right py-4 px-4 font-mono text-sm">{formatReturn(scheme['1y'])}</td>
                        <td className="text-right py-4 px-4 font-mono text-sm">{formatReturn(scheme['3y'])}</td>
                        <td className="text-right py-4 px-4 font-mono text-sm">{formatReturn(scheme['5y'])}</td>
                        <td className="text-right py-4 px-4 font-mono text-sm">{formatReturn(scheme['10y'])}</td>
                        <td className="text-right py-4 px-4 font-mono text-sm">{formatReturn(scheme.inception)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs">
              * Past performance is not indicative of future results. Returns are in CAGR terms.
              Data sourced from AMFI. Last updated: {new Date().toLocaleDateString('en-IN')}
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SchemePerformancePage;
